/**
 * The IdeaBrief mark, animated for the scan.
 *
 * The square rotates while a highlight travels the three dots in reading order
 * — top-left, top-right, bottom-right — the brand doing the work rather than a
 * generic spinner parked next to it. The pulse shares the rotation's duration
 * and easing so the whole mark reads as one object, and the L-shaped path means
 * the travel stays legible through the spin.
 *
 * The dots do NOT move: with three of them the earlier swap had no unambiguous
 * partner to trade with, and the geometry leaves only a 2px gap at 18px, so any
 * translation collides. Brightness carries the motion instead.
 *
 * Geometry is derived from `size` exactly as in `Logo`, so the two stay in step
 * if the mark is ever adjusted. `prefers-reduced-motion` (handled globally in
 * tokens.css) collapses this to the ordinary static logo.
 */
const CYCLE = '1.8s';
const EASE = 'cubic-bezier(.65,.05,.35,.95)';

/** CYCLE in seconds, so the per-dot delays can't drift from the duration. */
const SECONDS = parseFloat(CYCLE);

export function ScanningMark({
  size = 34,
  spinning = true,
}: {
  size?: number;
  /** False renders the resting mark — used for completed steps. */
  spinning?: boolean;
}) {
  const dot = Math.round(size * 0.25);
  const inset = Math.round(size * 0.17);
  const secondTop = inset + Math.round(size * 0.38);

  const dotBase = {
    position: 'absolute' as const,
    width: dot,
    height: dot,
    borderRadius: '50%',
    background: '#fff',
    // The pulse scales the dot, so it must grow from the centre rather than the
    // default corner — otherwise each dot drifts toward its neighbour.
    transformOrigin: 'center' as const,
  };

  /**
   * Resting opacity matches `Logo` exactly: the top-left dot is the bright one,
   * the right-axis pair sits muted. When spinning, the keyframe overrides these
   * — they are what the mark falls back to for completed steps and under
   * reduced motion.
   */
  const positions = [
    { top: inset, left: inset, opacity: 0.9, delay: 0 },
    { top: inset, right: inset, opacity: 0.55, delay: 1 / 3 },
    { top: secondTop, right: inset, opacity: 0.55, delay: 2 / 3 },
  ];

  return (
    <div
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        borderRadius: size <= 20 ? 5 : 6,
        background: 'var(--acc)',
        position: 'relative',
        flex: 'none',
        animation: spinning ? `ibSpin ${CYCLE} ${EASE} infinite` : undefined,
      }}
    >
      {positions.map(({ delay, ...pos }, i) => (
        <div
          key={i}
          style={{
            ...dotBase,
            ...pos,
            // `backwards` matters: without it a delayed dot renders at its
            // default opacity of 1 until its turn comes, so the mark flashes
            // fully white for the first 1.2s instead of resting muted.
            animation: spinning
              ? `ibDotPulse ${CYCLE} ${EASE} ${(delay * SECONDS).toFixed(2)}s infinite backwards`
              : undefined,
          }}
        />
      ))}
    </div>
  );
}
